
// @ts-nocheck
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Package, Clock, DollarSign, Tag, ArrowRightLeft } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface OrderItem {
  id: string;
  product_name: string;
  variant_size: string;
  variant_color: string;
  quantity: number;
  unit_price: number;
  total_price: number;
}

interface Order {
  id: string;
  created_at: string;
  status: string;
  subtotal: number;
  tps: number;
  tvq: number;
  total: number;
  payment_status: string;
  items?: OrderItem[];
  order_number: number;
}

const OrderHistory = () => {
  const { data: orders, isLoading } = useQuery({
    queryKey: ["orders"],
    queryFn: async () => {
      console.log("Fetching orders history");
      const { data: orders, error } = await (supabase as any)
        .from("orders")
        .select(`
          *,
          items:order_items(*)
        `)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching orders:", error);
        throw error;
      }

      console.log("Orders fetched successfully:", orders);
      return orders as Order[];
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!orders?.length) {
    return (
      <div className="text-center py-12">
        <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-1">
          Aucune commande pour le moment
        </h3>
        <p className="text-gray-500">
          Vos commandes apparaîtront ici une fois que vous aurez effectué un achat.
        </p>
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    const variants: { [key: string]: { variant: "default" | "success" | "destructive" | "secondary" | "outline", label: string } } = {
      "pending": { variant: "secondary", label: "En attente" }, // Changed from "warning" to "secondary"
      "paid": { variant: "success", label: "Payée" },
      "refunded": { variant: "destructive", label: "Remboursée" },
      "failed": { variant: "destructive", label: "Échouée" }
    };

    const { variant, label } = variants[status] || variants.pending;

    return (
      <Badge variant={variant} className="font-medium">
        {label}
      </Badge>
    );
  };

  return (
    <div className="space-y-8">
      {orders.map((order) => (
        <div 
          key={order.id} 
          className="bg-white rounded-lg border border-gray-100 overflow-hidden transition-shadow hover:shadow-md"
        >
          <div className="bg-gray-50 border-b border-gray-100 p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Package className="h-4 w-4" />
                  <span>Commande #{order.order_number}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="h-4 w-4" />
                  <span>
                    {format(new Date(order.created_at), "d MMMM yyyy 'à' HH:mm", {
                      locale: fr,
                    })}
                  </span>
                </div>
              </div>
              <div className="flex flex-col md:items-end gap-2">
                <div className="flex items-center gap-2">
                  <ArrowRightLeft className="h-4 w-4 text-gray-500" />
                  {getStatusBadge(order.payment_status)}
                </div>
                <div className="flex items-center gap-2 font-medium">
                  <DollarSign className="h-4 w-4 text-gray-500" />
                  <span>{order.total.toFixed(2)} CAD</span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produit</TableHead>
                  <TableHead>Variante</TableHead>
                  <TableHead className="text-right">Prix unitaire</TableHead>
                  <TableHead className="text-right">Quantité</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {order.items?.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.product_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Tag className="h-4 w-4 text-gray-500" />
                        <span>{item.variant_size} - {item.variant_color}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {item.unit_price.toFixed(2)} CAD
                    </TableCell>
                    <TableCell className="text-right">{item.quantity}</TableCell>
                    <TableCell className="text-right font-medium">
                      {item.total_price.toFixed(2)} CAD
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-t-2">
                  <TableCell colSpan={4} className="text-right font-medium">
                    Sous-total:
                  </TableCell>
                  <TableCell className="text-right">
                    {order.subtotal.toFixed(2)} CAD
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={4} className="text-right text-gray-500">
                    TPS (5%):
                  </TableCell>
                  <TableCell className="text-right text-gray-500">
                    {order.tps.toFixed(2)} CAD
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={4} className="text-right text-gray-500">
                    TVQ (9.975%):
                  </TableCell>
                  <TableCell className="text-right text-gray-500">
                    {order.tvq.toFixed(2)} CAD
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={4} className="text-right font-medium">
                    Total:
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {order.total.toFixed(2)} CAD
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default OrderHistory;
